import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import Button from '@components-clean/core/button';
import Dialog from '@components-clean/core/dialog';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { InfoField } from '@components-clean/core/info-field';
import {
    DataViewConfigForm,
    IdTitleActive,
    PanelData,
} from '@pages-clean/admin/study/admin-data-views/data-views/types';
import { useTranslation } from '@hooks/index';

type Props = {
    onClose: () => void;
    onSubmit: (values: string[]) => void;
    nonFilterablePanelIds: string[];
    selectedPanels: string[];
    mainPanel: DataViewConfigForm['mainPanel'];
    studyPanels: PanelData[];
};

type PanelLists = {
    filterable: IdTitleActive[];
    nonFilterable: IdTitleActive[];
    modelPanels: string[];
};

type FormData = {
    nonFilterable: string[];
};

export const PanelFilteringDialog = ({
    onClose,
    onSubmit,
    nonFilterablePanelIds,
    selectedPanels,
    studyPanels,
    mainPanel,
}: Props) => {
    const { t } = useTranslation();

    const panels = useMemo(() => {
        const dataItems = studyPanels.reduce<PanelLists>(
            (items, panel) => {
                if (!selectedPanels.includes(panel.id)) return items;
                if (panel.isModel) {
                    items.modelPanels.push(panel.id);
                    return items;
                }

                if (nonFilterablePanelIds.includes(panel.id))
                    items.nonFilterable.push({ id: panel.id, title: panel.title, active: panel.active });
                else items.filterable.push({ id: panel.id, title: panel.title, active: panel.active });

                return items;
            },
            { filterable: [], nonFilterable: [], modelPanels: [] },
        );

        if (mainPanel && !mainPanel.isModel) {
            if (nonFilterablePanelIds.includes(mainPanel.id))
                dataItems.nonFilterable.push({
                    id: mainPanel.id,
                    title: mainPanel.title ?? '',
                    active: mainPanel.active,
                });
            else
                dataItems.filterable.push({ id: mainPanel.id, title: mainPanel.title ?? '', active: mainPanel.active });
        }

        dataItems.filterable.sort((a, b) => a.title.localeCompare(b.title));
        dataItems.nonFilterable.sort((a, b) => a.title.localeCompare(b.title));

        return dataItems;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [selectedPanels, nonFilterablePanelIds]);

    const transformResult = (values: FormData) => {
        const ids = panels.modelPanels.concat(values.nonFilterable);

        onSubmit(ids);
    };

    const {
        handleSubmit,
        setValue,
        formState: { isDirty },
    } = useForm({
        mode: 'all',
        defaultValues: {
            nonFilterable: panels.nonFilterable.map((p) => p.id),
        },
    });

    return (
        <Dialog
            data-testid="panel-filtering-dialog"
            title={t('admin:dataView.panelFilteringExceptions')}
            contentStyle={{ padding: '10px' }}
            width="820px"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ height: '80px' }}>
                    <InfoField message={t('admin:dataView.filterablePanelsInfo')} type="Info" />
                </div>
                <div style={{ height: '364px' }}>
                    <DragDropListsCore
                        list1Items={panels.nonFilterable}
                        list2Items={panels.filterable}
                        list1Header={t('admin:dataView.nonFilterablePanels')}
                        list2Header={t('admin:dataView.filterablePanels')}
                        onChangeItemsLeft={(values) => {
                            setValue(
                                'nonFilterable',
                                values.map((p) => p.id as string),
                                {
                                    shouldDirty: true,
                                },
                            );
                        }}
                        idField="id"
                        isTwoLists
                    />
                </div>
            </div>
            <DialogActionsBar layout="end">
                <Button onClick={onClose} data-testid="cancel-button">
                    {t('cancel')}
                </Button>
                <Button
                    disabled={!isDirty}
                    primary
                    onClick={() => handleSubmit(transformResult)()}
                    data-testid="submit-button"
                >
                    {t('update')}
                </Button>
            </DialogActionsBar>
        </Dialog>
    );
};
